import {useState, useEffect} from "react"
import {useLoginTranslation} from "@/i18n/hooks"
import {Link, useSearchParams} from "react-router-dom"
import {Heading} from "@/components/tailwind/heading"
import {Input} from "@/components/tailwind/input"
import {Button} from "@/components/tailwind/button"
import {Text} from "@/components/tailwind/text"
import {Divider} from "@/components/tailwind/divider"
import {validateResetToken, confirmPasswordReset} from "@/services/password-reset-services"
import ValidationAlert from "@/components/validation-alert"
import PasswordStrengthIndicator from "@/components/password-strength-indicator.tsx"
import type {ValidationIssue} from "@/types/problem-details"

// The reset-confirm page is unauthenticated, so it cannot fetch the org's effective password policy
// for a client-side gate. The backend remains authoritative for length, digit/special-char
// requirements, blocklist, HIBP and self-reference checks; this page surfaces the structured
// findings it returns and renders the zxcvbn-ts strength indicator with an empty userInputs list
// because username/real name are not available without a session.
type ResetConfirmError =
    | { kind: "field"; messages: string[] }
    | { kind: "form"; message: string }

type StructuredReject = {
    validationIssues?: ValidationIssue[]
    error?: string
    detail?: string
    title?: string
}

function isTokenRejection(reject: StructuredReject): boolean {
    const candidates = [reject.error, reject.detail, reject.title]
    return candidates.some((value) => value?.toLowerCase().includes("token"))
}

export default function PasswordResetConfirmPage() {
    const {t} = useLoginTranslation()
    const [searchParams] = useSearchParams()
    const token = searchParams.get("token") || ""

    const [isValidating, setIsValidating] = useState(true)
    const [isTokenValid, setIsTokenValid] = useState(false)
    const [newPassword, setNewPassword] = useState("")
    const [confirmPassword, setConfirmPassword] = useState("")
    const [isLoading, setIsLoading] = useState(false)
    const [isSuccess, setIsSuccess] = useState(false)
    const [error, setError] = useState<ResetConfirmError | null>(null)

    useEffect(() => {
        if (!token) {
            setIsValidating(false)
            setIsTokenValid(false)
            return
        }

        validateResetToken(token)
            .then(result => {
                setIsTokenValid(result.valid)
                setIsValidating(false)
            })
            .catch(() => {
                setIsTokenValid(false)
                setIsValidating(false)
            })
    }, [token])

    async function handleSubmit(e: React.FormEvent) {
        e.preventDefault()
        setError(null)

        // Presence is guarded client-side so that submitting empty fields surfaces a specific
        // message instead of the generic server-error fallback: @NotBlank on the request DTO is
        // rejected by GenericControllerAdvice as a fieldErrors ProblemDetail, a shape the
        // structured-issue handler below cannot classify. Matches the change-password dialog and
        // the forgot-password request page, both of which block empty input locally.
        if (newPassword.length === 0) {
            setError({kind: "form", message: t("forgotPassword.errors.newPasswordEmpty")})
            return
        }
        if (confirmPassword.length === 0) {
            setError({kind: "form", message: t("forgotPassword.errors.confirmPasswordEmpty")})
            return
        }

        if (newPassword !== confirmPassword) {
            setError({kind: "form", message: t("forgotPassword.errors.passwordMismatch")})
            return
        }

        setIsLoading(true)
        try {
            await confirmPasswordReset(token, newPassword)
            setIsSuccess(true)
        } catch (rejection) {
            handleRejection(rejection)
        } finally {
            setIsLoading(false)
        }
    }

    function handleRejection(rejection: unknown) {
        if (rejection instanceof Error) {
            // Legacy shape: the caller (or a test) threw a plain Error. Token messages still flip
            // the page into the invalid-token view; anything else falls back to the generic error.
            if (rejection.message.toLowerCase().includes("token")) {
                setIsTokenValid(false)
                return
            }
            setError({kind: "form", message: t("forgotPassword.errors.serverError")})
            return
        }

        const reject = (rejection ?? {}) as StructuredReject
        if (reject.validationIssues?.length) {
            const messages = reject.validationIssues.map((issue) => t(issue.message, issue.args))
            setError({kind: "field", messages})
            return
        }
        if (isTokenRejection(reject)) {
            setIsTokenValid(false)
            return
        }
        setError({kind: "form", message: t("forgotPassword.errors.serverError")})
    }

    if (isValidating) {
        return (
            <div className="mx-auto max-w-4xl" data-testid="password-reset-confirm-validating">
                <Heading level={2}>{t("forgotPassword.setPassword.headline")}</Heading>
                <Divider className="my-10 mt-6"/>
                <Text role="status" aria-busy="true">
                    {t("forgotPassword.setPassword.validating")}
                </Text>
            </div>
        )
    }

    if (!isTokenValid && !isSuccess) {
        return (
            <div className="mx-auto max-w-4xl">
                <Heading level={2}>{t("forgotPassword.setPassword.headline")}</Heading>
                <Divider className="my-10 mt-6"/>
                <div className="mb-6" data-testid="invalid-token-message">
                    <ValidationAlert message={t("forgotPassword.setPassword.invalidToken")}/>
                </div>
                <Link
                    to="/password-reset"
                    className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
                >
                    {t("forgotPassword.headline")}
                </Link>
            </div>
        )
    }

    if (isSuccess) {
        return (
            <div className="mx-auto max-w-4xl">
                <Heading level={2}>{t("forgotPassword.setPassword.headline")}</Heading>
                <Divider className="my-10 mt-6"/>
                <div className="bg-blue-50 dark:bg-blue-950 border-l-4 border-blue-400 dark:border-blue-500 p-4 mb-6"
                     data-testid="password-reset-confirm-success">
                    <Text className="text-blue-700 dark:text-blue-300">
                        {t("forgotPassword.setPassword.successMessage")}
                    </Text>
                </div>
                <Link
                    to="/login"
                    className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
                >
                    {t("forgotPassword.backToLogin")}
                </Link>
            </div>
        )
    }

    return (
        <div className="mx-auto max-w-4xl">
            <Heading level={2}>{t("forgotPassword.setPassword.headline")}</Heading>
            <Divider className="my-10 mt-6"/>

            <form onSubmit={handleSubmit} data-testid="password-reset-confirm-form">
                <Text className="mb-6">{t("forgotPassword.setPassword.description")}</Text>

                {error?.kind === "form" && (
                    <section className="mb-4" data-testid="password-reset-confirm-error">
                        <ValidationAlert message={error.message}/>
                    </section>
                )}

                <section className="grid gap-x-8 gap-y-6 sm:grid-cols-2">
                    <div className="space-y-1">
                        <Text>{t("forgotPassword.setPassword.form.newPassword.label")}</Text>
                    </div>
                    <div>
                        <Input
                            type="password"
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            placeholder={t("forgotPassword.setPassword.form.newPassword.placeholder")}
                            aria-label={t("forgotPassword.setPassword.form.newPassword.ariaLabel")}
                            disabled={isLoading}
                            data-testid="new-password-input"
                            data-invalid={error?.kind === "field" || null}
                        />
                        <PasswordStrengthIndicator password={newPassword}/>
                        {error?.kind === "field" && (
                            <div className="mt-2 space-y-1" data-testid="new-password-field-error">
                                {error.messages.map((message, index) => (
                                    <ValidationAlert key={index} message={message}/>
                                ))}
                            </div>
                        )}
                    </div>
                    <div className="space-y-1">
                        <Text>{t("forgotPassword.setPassword.form.confirmPassword.label")}</Text>
                    </div>
                    <div>
                        <Input
                            type="password"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            placeholder={t("forgotPassword.setPassword.form.confirmPassword.placeholder")}
                            aria-label={t("forgotPassword.setPassword.form.confirmPassword.ariaLabel")}
                            disabled={isLoading}
                            data-testid="confirm-password-input"
                        />
                    </div>
                </section>

                <section className="flex justify-end mt-6">
                    <Button
                        type="submit"
                        disabled={isLoading}
                        data-testid="confirm-password-submit"
                    >
                        {isLoading
                            ? t("forgotPassword.setPassword.form.submit.loading")
                            : t("forgotPassword.setPassword.form.submit.label")}
                    </Button>
                </section>
            </form>
        </div>
    )
}
